import { useEffect, useMemo } from 'react';

import { TimeService } from '@/utils/services/TimeService';

import { useAlbumContext } from '@/utils/hooks/context/useAlbumContext';
import useDateLimits from '@/utils/hooks/datepicker/useDateLimits';
import useGroupMedias from '@/utils/hooks/swr/group/useGroupMedias';
import useMedias from '@/utils/hooks/swr/useMedias';
import useUser from '@/utils/hooks/swr/useUser';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useIsGroup from '@/utils/hooks/user/useIsGroup';

type UseAlbumPostsOptions = {
    revalidateOnFocus?: boolean;
    revalidateOnReconnect?: boolean;
};

const useAlbumPosts = (options: UseAlbumPostsOptions = {}) => {
    const { albumInfo } = useAlbumContext();
    const { getMinDate, getMaxDate } = useDateLimits();
    const { activeSocialProfile } = useActiveSocialProfile();
    const { activeGroup } = useActiveGroup();
    const isGroup = useIsGroup();
    const { user } = useUser();

    // Base params for single SP
    const baseSingleSPParams = useMemo(() => {
        if (!activeSocialProfile) return null;

        return {
            period: 'custom',
            with_labels: true,
            start: TimeService.formatDateForAPI(
                albumInfo?.criteria?.started_at || getMinDate(activeSocialProfile)
            ),
            end: TimeService.formatDateForAPI(
                albumInfo?.criteria?.ended_at || getMaxDate(activeSocialProfile)
            ),
            filter_labels: albumInfo?.criteria?.labels ?? [],
            filter_hashtags: albumInfo?.criteria?.hashtags ?? [],
            filter_post_type: albumInfo?.criteria?.post_types ?? []
        };
    }, [
        albumInfo?.criteria?.started_at,
        albumInfo?.criteria?.ended_at,
        albumInfo?.criteria?.labels,
        albumInfo?.criteria?.hashtags,
        albumInfo?.criteria?.post_types,
        activeSocialProfile,
        getMinDate,
        getMaxDate
    ]);

    // Base params for group (no activeSocialProfile check - groups don't have one)
    const baseGroupParams = useMemo(() => {
        if (!activeGroup) return null;

        return {
            start: TimeService.formatDateForGroup(
                albumInfo?.criteria?.started_at || getMinDate(),
                user?.settings?.timezone
            ),
            end: TimeService.formatDateForGroup(
                albumInfo?.criteria?.ended_at || getMaxDate(),
                user?.settings?.timezone
            ),
            with_labels: true,
            with_albums: false,
            filter_hashtags: albumInfo?.criteria?.hashtags ?? [],
            filter_social_profiles: albumInfo?.criteria?.social_profiles ?? [],
            filter_post_type:
                albumInfo?.criteria?.post_types?.length > 0
                    ? albumInfo?.criteria?.post_types
                    : undefined
        };
    }, [
        albumInfo?.criteria?.started_at,
        albumInfo?.criteria?.ended_at,
        albumInfo?.criteria?.hashtags,
        albumInfo?.criteria?.social_profiles,
        albumInfo?.criteria?.post_types,
        activeGroup,
        user?.settings?.timezone,
        getMinDate,
        getMaxDate
    ]);

    // Use different hooks based on group/single SP
    const singleSPResult = useMedias({
        activeSPID: !isGroup && activeSocialProfile ? activeSocialProfile.id : null,
        groupID: null,
        params: !isGroup && activeSocialProfile ? baseSingleSPParams : null,
        options: {
            revalidateOnFocus: options.revalidateOnFocus ?? false,
            revalidateOnReconnect: options.revalidateOnReconnect ?? false
        }
    });

    const groupResult = useGroupMedias({
        groupId: isGroup ? activeGroup?.id : null,
        limit: 20,
        params: isGroup ? baseGroupParams : null,
        options: {
            revalidateOnFocus: options.revalidateOnFocus ?? false,
            revalidateOnReconnect: options.revalidateOnReconnect ?? false
        }
    });

    const {
        dataMedias: rawDataMedias,
        isLoadingInitialData,
        isLoadingMore,
        isLoadMore,
        loadMoreMedia,
        setRequestParams,
        mutateMedias
    } = isGroup ? groupResult : singleSPResult;

    // Normalize data structure and apply client-side filtering for labels only
    const dataMedias = useMemo(() => {
        if (!isGroup) return rawDataMedias;

        return rawDataMedias?.map((page) => {
            const selectedLabels = albumInfo?.criteria?.labels || [];
            let filteredMedias = page.medias;

            // Filter by labels if any are selected (API doesn't support filter_labels for groups)
            if (selectedLabels.length > 0) {
                filteredMedias = filteredMedias?.filter(
                    (media: any) =>
                        media.labels?.some((label: any) => selectedLabels.includes(label.label))
                );
            }

            return {
                medias: filteredMedias,
                stories: filteredMedias
            };
        });
    }, [isGroup, rawDataMedias, albumInfo?.criteria?.labels]);

    // Update request params when criteria change
    useEffect(() => {
        if (!setRequestParams) return;

        if (!isGroup) {
            if (!activeSocialProfile) return;
            setRequestParams((prevState: any) => ({
                ...prevState,
                start: TimeService.formatDateForAPI(
                    albumInfo?.criteria?.started_at || getMinDate(activeSocialProfile)
                ),
                end: TimeService.formatDateForAPI(
                    albumInfo?.criteria?.ended_at || getMaxDate(activeSocialProfile)
                ),
                filter_labels: albumInfo?.criteria?.labels ?? [],
                filter_hashtags: albumInfo?.criteria?.hashtags ?? [],
                filter_post_type: albumInfo?.criteria?.post_types ?? []
            }));
        } else {
            if (!activeGroup) return;
            setRequestParams((prevState: any) => ({
                ...prevState,
                start: TimeService.formatDateForGroup(
                    albumInfo?.criteria?.started_at || getMinDate(),
                    user?.settings?.timezone
                ),
                end: TimeService.formatDateForGroup(
                    albumInfo?.criteria?.ended_at || getMaxDate(),
                    user?.settings?.timezone
                ),
                filter_hashtags: albumInfo?.criteria?.hashtags ?? [],
                filter_social_profiles: albumInfo?.criteria?.social_profiles ?? [],
                filter_post_type:
                    albumInfo?.criteria?.post_types?.length > 0
                        ? albumInfo?.criteria?.post_types
                        : undefined
            }));
        }
    }, [
        albumInfo?.criteria?.started_at,
        albumInfo?.criteria?.ended_at,
        albumInfo?.criteria?.labels,
        albumInfo?.criteria?.hashtags,
        albumInfo?.criteria?.post_types,
        albumInfo?.criteria?.social_profiles,
        isGroup,
        setRequestParams,
        getMinDate,
        getMaxDate,
        activeSocialProfile,
        activeGroup,
        user?.settings?.timezone
    ]);

    return {
        dataMedias,
        isLoadingInitialData,
        isLoadingMore,
        isLoadMore,
        loadMoreMedia,
        mutateMedias
    };
};

export { useAlbumPosts };
